import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { http } from '@/system/http';
import { languages, TranscriptionProvider } from '@/system/components/global/ai/constants';
import { getQueryParam } from '@/system/helpers';

// Move state outside the function to make it shared across all instances
const language = ref(null);
const provider = ref(null);
const showTimestamps = ref(true);
const audioDevice = ref(null);

// Track if the composable has been initialized
let isInitialized = false;
let defaultSettingsLoaded = false;

/**
 * Composable for managing transcription settings
 * @returns {Object} Transcription settings and related methods
 */
export default function useTranscriptionSettings() {
  const store = useStore();
  
  /**
   * All supported languages for transcription
   */
  const supportedLanguages = computed(() => {
    if (!store.state.transcriptions?.enabled) {
      return [];
    }
    
    return languages.filter((lang) => {
      return (store.state.transcriptions?.available_languages || []).includes(lang.id);
    });
  });
  
  /**
   * The default language based on user preferences
   */
  const defaultLanguage = computed(() => {
    return supportedLanguages.value.find((lang) => {
      return store.state.auth.user.language === lang.country;
    });
  });
  
  /**
   * Load settings from store or apply defaults
   */
  const loadSettings = () => {
    // Language
    const userSettingLanguage = supportedLanguages.value.find((lang) => {
      return store.state.transcriptions?.settings?.language === lang.id;
    });
    language.value = userSettingLanguage || defaultLanguage.value;
    
    // Provider - check URL param first
    if (getQueryParam('transcription_provider')) {
      const providerFromQuery = String(getQueryParam('transcription_provider')).toLowerCase();
      if (Object.values(TranscriptionProvider).includes(providerFromQuery)) {
        provider.value = providerFromQuery;
      }
    }
    
    // If not from URL, get from store
    if (!provider.value) {
      provider.value = store.state.transcriptions?.settings?.provider || TranscriptionProvider.Speechmatics;
    }
    
    // Timestamps
    if (store.state.transcriptions?.settings?.show_timestamps !== null) {
      showTimestamps.value = store.state.transcriptions?.settings?.show_timestamps;
    }

    // Audio device
    if (store.state.transcriptions?.settings?.device_id) {
      audioDevice.value = store.state.transcriptions?.settings?.device_id;
    }
    
    // Use nextTick to ensure all reactivity updates have processed
    // before we allow watchers to send updates
    setTimeout(() => {
        defaultSettingsLoaded = true;
    }, 0);
  };
  
  /**
   * Save settings to backend
   * @param {Object} data - Settings to update
   */
  const updateSettings = (data = {}) => {
    if (!defaultSettingsLoaded) {
      return;
    }

    http.post('/app/ai/transcription-settings', data);
  };
  
  // Initialize only once
  if (!isInitialized) {
    // Set up watchers to automatically save changes
    watch(language, (newVal) => {
      if (newVal) {
        updateSettings({ language: newVal.id });
      }
    });
    
    watch(provider, (newVal) => {
      if (newVal) {
        updateSettings({ provider: newVal });
      }
    });
    
    watch(showTimestamps, (newVal) => {
      updateSettings({ show_timestamps: newVal });
    });
    
    watch(audioDevice, (newVal) => {
      if (newVal) {
        updateSettings({ device_id: newVal.deviceId });
      }
    });
    
    // Initialize on creation
    loadSettings();
    
    isInitialized = true;
  }
  
  return {
    // Settings
    language,
    provider,
    showTimestamps,
    audioDevice,
    
    // Computed properties
    supportedLanguages,
    defaultLanguage,
  };
} 